import { ButtonComponent } from '@/components/commons'
import { InputComponent } from '@/components/commons/forms/input/Input.component'
import { Title } from '@/components/commons/typography/Title'
import { Modal } from '@/components/modal/Modal'
import { CloseIcon } from '@/public/assets/v2/images/icons'
import { environment } from '@/shared/consts/environment'
import { cssClass } from '@/shared/utils'

import { shareModalSize } from 'containers/highlights/styles/highlight.css'
import { Highlight } from 'containers/highlights/types/highlights'
import { useProfile } from 'containers/profile/hooks/useProfile'
import { joinWithSlashExternal } from 'utilities/links'
import { useRef, useCallback } from 'react'
import { User } from 'containers/user/types/user'

interface HighlightShareModalProps {
  highlight: Highlight
  closeModal: () => void
  isOpen: boolean
  overrideProfile?: User
}

export const HighlightShareModal = ({
  highlight,
  closeModal,
  isOpen,
  overrideProfile,
}: HighlightShareModalProps) => {
  const buttonRef = useRef(null)
  const { profile: currentProfile } = useProfile()
  const profile = overrideProfile || currentProfile

  const onCopy = useCallback(() => {
    navigator.clipboard.writeText(
      joinWithSlashExternal(
        environment.appUrl,
        profile?.usernameLower,
        'highlights',
        highlight?.id
      )
    )

    buttonRef.current.innerText = 'COPIED!'

    setTimeout(() => {
      closeModal()
    }, 300)
  }, [profile, highlight, closeModal])

  return (
    <Modal
      className="--v2 full-w p-0 z-50"
      onClose={closeModal}
      opened={isOpen}
      withCloseButton={false}
      classNames={{
        body: 'p-0 rounded-12',
        content: cssClass(['rounded-12 full-w', shareModalSize]),
      }}
      size="auto"
      centered
      overlayProps={{
        backgroundOpacity: 0.4,
        blur: 5,
      }}
    >
      <div className="relative p-40">
        <div className="absolute top-16 right-16">
          <CloseIcon className="cursor-pointer text-22" onClick={closeModal} />
        </div>
        <Title className="text-center">Share This Highlight</Title>
        <div className="mt-20 flex items-center gap-12">
          <InputComponent
            size="small"
            disabled
            value={joinWithSlashExternal(
              environment.appUrl,
              profile?.username?.toLocaleLowerCase(),
              'highlights',
              highlight?.id
            )}
            className={['flex-grow cursor-pointer']}
            onClick={onCopy}
          />
          <ButtonComponent
            ref={buttonRef}
            size="small"
            variation="primary"
            label="Copy"
            onClick={onCopy}
          />
        </div>
      </div>
    </Modal>
  )
}
